import { useMemo, useState, useEffect, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../../lib/mcp-client";
import { useListTree, useMoveNode, useWorkspaceInfo } from "../../../lib/hooks/use-kuvio";
import { useAuth } from "../../../lib/hooks/use-auth";
import { showToast } from "../primitives";
import type { LibraryNode } from "../../../lib/types";

interface MoveToModalProps {
  open: boolean;
  onClose: () => void;
  /** Node(s) being moved. One for a single move, many for a bulk move. */
  sources: LibraryNode[];
  /** Fired after every source has been moved. */
  onMoved?: (newParentId: string) => void;
}

interface Candidate {
  id: string;
  name: string;
  type: string;
  parent_id: string | null;
  path: string;
}

export function MoveToModal({ open, onClose, sources, onMoved }: MoveToModalProps) {
  const { currentWorkspaceId } = useAuth();
  const workspace = useWorkspaceInfo();
  const agents = useListTree(currentWorkspaceId ?? undefined);
  // Folders aren't in the agent-only tree; fetch them separately. Both queries
  // are type-filtered so neither overflows the Durable Object result limit.
  const folders = useQuery({
    queryKey: ["library-tree", "folder"],
    queryFn: () => mcpClient.listTree({ depth: -1, type: "folder" }),
    enabled: open && !!currentWorkspaceId,
    staleTime: 15_000,
  });
  const moveNode = useMoveNode();

  const [search, setSearch] = useState("");
  const [targetId, setTargetId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSearch("");
      setTargetId(null);
      setBusy(false);
    }
  }, [open]);

  const rootNodeId = workspace.data?.root_node_id as string | undefined;
  const workspaceName = workspace.data?.workspace?.name ?? "Workspace";

  // Candidate parents: workspace root + every agent + every folder, minus the
  // sources themselves and any of their descendants (cycle prevention).
  const candidates = useMemo<Candidate[]>(() => {
    const byId = new Map<string, Candidate>();
    const add = (n: LibraryNode) => {
      if (n.type !== "agent" && n.type !== "folder") return;
      byId.set(n.id, { id: n.id, name: n.name, type: n.type, parent_id: n.parent_id, path: n.path });
    };
    (agents.data ?? []).forEach(add);
    (folders.data ?? []).forEach(add);

    // Ensure a workspace-root option exists even if list_tree omitted it.
    if (rootNodeId && !byId.has(rootNodeId)) {
      byId.set(rootNodeId, { id: rootNodeId, name: workspaceName, type: "agent", parent_id: null, path: `/${rootNodeId}` });
    }

    const sourceIds = new Set(sources.map((s) => s.id));
    const sourcePaths = sources.map((s) => s.path);
    const isBlocked = (c: Candidate) =>
      sourceIds.has(c.id) || sourcePaths.some((p) => c.path === p || c.path.startsWith(p + "/"));

    return Array.from(byId.values())
      .filter((c) => !isBlocked(c))
      .sort((a, b) => {
        if (a.id === rootNodeId) return -1;
        if (b.id === rootNodeId) return 1;
        return a.path.localeCompare(b.path);
      });
  }, [agents.data, folders.data, rootNodeId, workspaceName, sources]);

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    candidates.forEach((c) => m.set(c.id, c.name));
    if (rootNodeId) m.set(rootNodeId, workspaceName);
    return m;
  }, [candidates, rootNodeId, workspaceName]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) => c.name.toLowerCase().includes(q));
  }, [candidates, search]);

  if (!open) return null;

  const target = candidates.find((c) => c.id === targetId) ?? null;

  async function handleConfirm() {
    if (!target) return;
    setBusy(true);
    try {
      for (const src of sources) {
        await moveNode.mutateAsync({ id: src.id, new_parent_id: target.id });
      }
      showToast(
        sources.length === 1
          ? `Moved '${sources[0]!.name}' to '${target.name}'`
          : `Moved ${sources.length} items to '${target.name}'`,
      );
      onMoved?.(target.id);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Move failed";
      showToast(`Couldn't move: ${msg}`, "error", true);
    } finally {
      setBusy(false);
    }
  }

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    fontFamily: "var(--font-sans)",
  };

  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-lg)",
    padding: 24,
    width: 460,
    maxWidth: "90vw",
    display: "flex",
    flexDirection: "column",
    maxHeight: "80vh",
  };

  const input: CSSProperties = {
    width: "100%",
    fontSize: 13,
    padding: "8px 10px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
    marginBottom: 12,
  };

  const heading = sources.length === 1 ? `Move ${sources[0]!.name}` : `Move ${sources.length} items`;
  const loading = agents.isLoading || folders.isLoading;

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div style={modal} role="dialog" aria-label="Move to">
        <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4, color: "var(--color-text-primary)" }}>
          {heading}
        </div>
        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 16 }}>
          Pick a new parent. Folders and agents can hold children; the source and its descendants
          are hidden to prevent loops.
        </div>

        <input
          type="text"
          autoFocus
          style={input}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search folders and agents…"
        />

        <div style={{ overflowY: "auto", flex: 1, minHeight: 0, margin: "0 -4px" }}>
          {loading ? (
            <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", padding: "12px 4px" }}>
              Loading destinations…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", padding: "12px 4px" }}>
              No matching destinations.
            </div>
          ) : (
            filtered.map((c) => {
              const selected = c.id === targetId;
              const parentName = c.id === rootNodeId ? null : (c.parent_id ? nameById.get(c.parent_id) : null);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setTargetId(c.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    textAlign: "left",
                    padding: "8px",
                    border: "none",
                    borderRadius: "var(--border-radius-sm)",
                    background: selected ? "var(--color-background-info-subtle, var(--color-background-secondary))" : "transparent",
                    cursor: "pointer",
                    fontFamily: "var(--font-sans)",
                    boxShadow: selected ? "inset 0 0 0 1px var(--color-text-info)" : "none",
                  }}
                >
                  <i
                    className={`ti ti-${c.type === "folder" ? "folder" : "robot"}`}
                    style={{ fontSize: 15, color: "var(--color-text-secondary)", flexShrink: 0 }}
                    aria-hidden="true"
                  />
                  <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>
                    {c.id === rootNodeId ? `${c.name} (root)` : c.name}
                  </span>
                  {parentName && (
                    <span style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginLeft: "auto" }}>
                      in {parentName}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16, alignItems: "center" }}>
          {target && (
            <span style={{ fontSize: 12, color: "var(--color-text-secondary)", marginRight: "auto" }}>
              Destination: <strong>{target.name}</strong>
            </span>
          )}
          <button type="button" onClick={onClose} disabled={busy} style={{ fontSize: 13, padding: "8px 14px" }}>
            Cancel
          </button>
          <button
            type="button"
            data-variant="primary"
            onClick={handleConfirm}
            disabled={!target || busy}
            style={{ fontSize: 13, padding: "8px 14px" }}
          >
            {busy ? "Moving…" : "Move here"}
          </button>
        </div>
      </div>
    </div>
  );
}
